import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ResizeObserverPolyfill from 'resize-observer-polyfill';
import { NavigationItemType } from 'framework/navigation';
import useHeaderMenuService from '../headerMenuService';

export const OVERFLOW_BUTTON_WIDTH = 40;

interface Measurement {
  data: NavigationItemType[];
  pathname: string;
  visibleIndices: number[];
}

const useDesktopHeaderMenuItemsService = () => {
  const { getMenuData } = useHeaderMenuService();
  const data = getMenuData();
  const { pathname } = useLocation();
  const [measurement, setMeasurement] = useState<Measurement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMeasuring = !measurement || measurement.data !== data || measurement.pathname !== pathname;

  useLayoutEffect(() => {
    const container = containerRef.current;
    const menu = container?.parentElement;
    if (!isMeasuring || !container || !menu || !data.length) return;

    // All tabs are rendered during measurement, including those previously in overflow.
    const tabs = Array.from(container.children) as HTMLElement[];
    const width = menu.getBoundingClientRect().width;
    const widths = tabs.map(tab => tab.getBoundingClientRect().width);
    const totalWidth = widths.reduce((total, tabWidth) => total + tabWidth, 0);
    let visibleIndices: number[];

    if (totalWidth <= width) {
      visibleIndices = tabs.map((_, index) => index);
    } else {
      const availableWidth = Math.max(0, width - OVERFLOW_BUTTON_WIDTH);
      const activeIndex = tabs.findIndex(tab => tab.querySelector('.header-menu__item-link.active'));
      const priority = [...(activeIndex === -1 ? [] : [activeIndex]), ...tabs.map((_, index) => index)].filter(
        (index, position, indices) => indices.indexOf(index) === position,
      );
      let usedWidth = 0;
      visibleIndices = priority.filter(index => {
        if (usedWidth + widths[index] > availableWidth) return false;
        usedWidth += widths[index];
        return true;
      });
    }

    setMeasurement({ data, pathname, visibleIndices });
  }, [data, isMeasuring, pathname]);

  useEffect(() => {
    const menu = containerRef.current?.parentElement;
    if (!menu) return;

    let previousWidth = menu.getBoundingClientRect().width;
    const remeasure = () => {
      const width = menu.getBoundingClientRect().width;
      if (width !== previousWidth) {
        previousWidth = width;
        setMeasurement(null);
      }
    };
    const Observer = globalThis.ResizeObserver || ResizeObserverPolyfill;
    const observer = new Observer(remeasure);
    observer.observe(menu);
    window.addEventListener('resize', remeasure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', remeasure);
    };
  }, []);

  const visibleIndices = isMeasuring ? null : measurement.visibleIndices;
  return {
    visibleTabs: data.filter((_, index) => !visibleIndices || visibleIndices.includes(index)),
    overflowTabs: visibleIndices ? data.filter((_, index) => !visibleIndices.includes(index)) : [],
    containerRef,
  };
};

export default useDesktopHeaderMenuItemsService;
